import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { flagsApi, type FeatureStage } from '../api/flagsApi'
import { toast } from '@/hooks/use-toast'

export const featureKeys = {
  mine: ['features', 'mine'] as const,
  all: ['features', 'all'] as const,
  grants: (key: string) => ['features', 'grants', key] as const,
}

/**
 * The signed-in account's flags.
 *
 * Long `staleTime` on purpose: this gates navigation, so refetching it mid-session would make
 * menu items appear and disappear under the cursor. A stage change reaches an artist on their
 * next load, which is the right granularity for a release gate.
 */
export function useFeatures() {
  const query = useQuery({
    queryKey: featureKeys.mine,
    queryFn: flagsApi.mine,
    staleTime: 5 * 60 * 1000,
  })

  return {
    features: query.data?.features ?? {},
    /**
     * Unknown key -> false. A flag that has not been registered is not enabled; that is what makes
     * it safe to wrap a surface in a gate before anyone has created its flag row.
     */
    isEnabled: (key: string) => Boolean(query.data?.features?.[key]),
    /**
     * True only while a manager is viewing as an artist, for a flag that is on because of the
     * manager. The artist being viewed does not have it, so the shell should say so.
     */
    isStaffOnly: (key: string) => Boolean(query.data?.staffOnly?.includes(key)),
    /** While the first fetch is in flight nothing is known, so nothing gated should render yet. */
    isLoading: query.isLoading,
  }
}

// ── Manager administration ───────────────────────────────────────────────────

export function useAllFeatures() {
  return useQuery({ queryKey: featureKeys.all, queryFn: flagsApi.list })
}

export function useFeatureGrants(key: string | null) {
  return useQuery({
    queryKey: featureKeys.grants(key ?? ''),
    queryFn: () => flagsApi.grants(key as string),
    enabled: Boolean(key),
  })
}

const STAGE_SAID: Record<FeatureStage, string> = {
  off: 'Nobody can see this now, including managers.',
  internal: 'Managers and anyone granted early access can see this.',
  preview: 'Only managers and the accounts you have granted can see this.',
  ga: 'Everyone can see this.',
}

export function useSetFeatureStage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ key, stage }: { key: string; stage: FeatureStage }) => flagsApi.setStage(key, stage),
    onSuccess: (_, { stage }) => {
      qc.invalidateQueries({ queryKey: featureKeys.all })
      qc.invalidateQueries({ queryKey: featureKeys.mine })
      // Naming the audience is the point of the toast: "stage updated" would not tell a manager
      // whether they just shipped something to 1,300 artists.
      toast({ title: `Moved to ${stage}`, description: STAGE_SAID[stage] })
    },
    onError: (e: any) =>
      toast({
        title: 'Stage not changed',
        description: e?.response?.data?.message ?? 'Could not change the stage.',
        variant: 'destructive',
      }),
  })
}

export function useGrantFeature() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ key, userId, note }: { key: string; userId: number; note?: string }) =>
      flagsApi.grant(key, userId, note),
    onSuccess: (_, { key }) => {
      qc.invalidateQueries({ queryKey: featureKeys.grants(key) })
      qc.invalidateQueries({ queryKey: featureKeys.all })
      toast({ title: 'Early access granted', description: 'They will see it on their next load.' })
    },
    onError: (e: any) =>
      toast({
        title: 'Not granted',
        description: e?.response?.data?.message ?? 'Could not grant access.',
        variant: 'destructive',
      }),
  })
}

export function useRevokeFeature() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ key, userId }: { key: string; userId: number }) => flagsApi.revoke(key, userId),
    onSuccess: (_, { key }) => {
      qc.invalidateQueries({ queryKey: featureKeys.grants(key) })
      qc.invalidateQueries({ queryKey: featureKeys.all })
      toast({ title: 'Early access removed' })
    },
  })
}
